import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Checks whether WebGL is available in the current browser.
 * Returns false if the browser cannot create a WebGL context at all
 * (e.g. GPU disabled, too many active contexts, unsupported hardware).
 */
export const isWebGLAvailable = (): boolean => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');

    if (!gl) return false;

    // Lose the context immediately so we don't hold a slot
    const ext = gl.getExtension('WEBGL_lose_context');

    if (ext) ext.loseContext();

    return true;
  } catch {
    return false;
  }
};

interface WebGLErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback UI when WebGL fails. Defaults to nothing. */
  fallback?: ReactNode;
}

interface WebGLErrorBoundaryState {
  hasError: boolean;
}

/**
 * React Error Boundary that catches WebGL-related crashes
 * (e.g. null context in Three.js) and renders a fallback instead
 * of letting the error propagate and crash the entire page.
 */
class WebGLErrorBoundary extends Component<
  WebGLErrorBoundaryProps,
  WebGLErrorBoundaryState
> {
  constructor(props: WebGLErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): WebGLErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn(
      'WebGL rendering failed, falling back gracefully:',
      error.message,
      info
    );

    Sentry.captureMessage(
      'WebGL rendering failed — graceful fallback activated',
      {
        level: 'warning',
        extra: {
          errorMessage: error.message,
          componentStack: info.componentStack
        }
      }
    );
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }

    return this.props.children;
  }
}

export default WebGLErrorBoundary;
